import { getPatient } from '../lib/api'
import storage from '../lib/storage'
import { fetchWithTimeout } from '../fetcher/fetcher-timeout'
import { API_URL, MUSIC_URL } from '../fetcher/constants'
import { examFactory } from './functions'

const HLS_TYPE = 'application/x-mpegURL'
const MP4_TYPE = 'video/mp4'

// A primeira linha que não é tag é a primeira URI do manifesto, resolvida
// contra a URL final dele (cobre URI relativa e redirect). Sem ela não há o
// que testar na Kaltura, então conta como falha.
const resolveFirstUri = (manifest, baseUrl) => {
    const firstUri = manifest
        .split('\n')
        .map((line) => line.trim())
        .find((line) => line && !line.startsWith('#'))
    if (!firstUri) {
        throw new Error('M3U8 sem segmentos')
    }
    return new URL(firstUri, baseUrl).href
}

// Prazo do teste inteiro (token, manifestos e segmento). O fetchWithTimeout só
// cobre até os cabeçalhos, e os fetch do token e do manifesto não têm prazo:
// sem isso um corpo travado deixaria a página em "Carregando..." para sempre.
export const KALTURA_CHECK_TIMEOUT_MS = 10000

// Testa o music-api e a Kaltura antes de tocar. Retorna a URL do vod.m3u8
// (vazia se o music-api caiu) e se o primeiro segmento da Kaltura respondeu.
const checkKaltura = async (examId, cycle) => {
    let kalturaM3u8 = ''
    let timer
    const deadline = new Promise((_, reject) => {
        timer = setTimeout(
            () => reject(new Error('Teste da Kaltura excedeu o prazo')),
            KALTURA_CHECK_TIMEOUT_MS
        )
    })

    const check = async () => {
        const response = await fetch(`${API_URL}/patient/media/${examId}/${cycle}/vmusic-token`, {
            headers: {
                Authorization: `Bearer ${storage.getToken()}`,
            },
        })
        if (!response.ok) {
            throw new Error('vmusic-token não disponível')
        }
        const vmusicToken = await response.text()
        const manifestUrl = `${MUSIC_URL}/v1/vlab/m3u8/${examId}/${vmusicToken}/vod.m3u8`

        const manifestResponse = await fetch(manifestUrl)
        if (!manifestResponse.ok) {
            throw new Error('M3U8 não disponível')
        }
        kalturaM3u8 = manifestUrl

        // Num manifesto mestre a primeira URI é uma variante: segue ela para
        // testar um segmento de verdade, e não só outra playlist.
        let manifest = await manifestResponse.text()
        let baseUrl = manifestResponse.url || manifestUrl
        if (manifest.includes('#EXT-X-STREAM-INF')) {
            const variantUrl = resolveFirstUri(manifest, baseUrl)
            const variantResponse = await fetchWithTimeout(variantUrl, 3000)
            manifest = await variantResponse.text()
            baseUrl = variantResponse.url || variantUrl
        }
        await fetchWithTimeout(resolveFirstUri(manifest, baseUrl), 3000)
    }

    try {
        await Promise.race([check(), deadline])
        return { kalturaM3u8, kalturaOk: true }
    } catch (e) {
        console.error('Falha no music-api/Kaltura, usando fallback:', e)
        return { kalturaM3u8, kalturaOk: false }
    } finally {
        clearTimeout(timer)
    }
}

// Ordem de fallback:
//   music + Kaltura ok       -> Kaltura, MP4, TS
//   só a Kaltura caiu        -> MP4, TS, Kaltura (o teste de 3s pode ter sido só lentidão)
//   music caiu               -> MP4, TS
// Fontes sem URL são puladas. A primeira vira src e o resto vai para
// secondarySrcs, que o Player consome quando a source atual dá erro fatal.
export const buildSourceChain = ({ kalturaM3u8, kalturaOk, mp4Url, m3u8 }) => {
    const kaltura = { src: kalturaM3u8, type: HLS_TYPE }
    const mp4 = { src: mp4Url, type: MP4_TYPE }
    const ts = { src: m3u8, type: HLS_TYPE }

    const chain = kalturaOk ? [kaltura, mp4, ts] : [mp4, ts, kaltura]
    return chain.filter(
        (source, index) => source.src && chain.findIndex(({ src }) => src === source.src) === index
    )
}

export const loadExamSource = async () => {
    const { payment, data } = await getPatient()

    if (payment) {
        return { paymentRedirect: payment }
    }

    const { introURL, m3u8, mp4Url, date, examId, cycle, examStatus } = examFactory(data)
    const { kalturaM3u8, kalturaOk } = await checkKaltura(examId, cycle)
    const [primary, ...secondary] = buildSourceChain({ kalturaM3u8, kalturaOk, mp4Url, m3u8 })

    if (!primary) {
        return {
            patient: { intro: '', src: '', type: MP4_TYPE, secondarySrcs: [], date, examStatus },
        }
    }

    return {
        patient: {
            intro: introURL || '',
            src: primary.src,
            type: primary.type,
            secondarySrcs: secondary.map(({ src }) => src),
            date,
            examStatus,
        },
    }
}
